import { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import { PudoLocation } from '@pperform/pp-data-mappers';
import { useTranslation } from 'react-i18next';
import { useDetectDevice } from 'hooks';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from './DropOffList.styles';
import {
  DropOffCardProps,
  DropOffCardButton,
  DropOffCardPanel,
} from '../DropOffCard';
import { PostalCodeInstruction } from '../PostalCodeInstruction';

interface DropOffListProps {
  dropOffs: DropOffCardProps[];
  isLoading: boolean;
  error: string;
  onDropOffClick: (dropOffId: string) => void;
  onSelectDropOff?: (dropOff: PudoLocation) => void;
  isViewOnly?: boolean;
  isShowDropoffMap?: boolean;
}

const DEFAULT_EXPANDED_INDEX = 0;

const DropOffList: FunctionComponent<DropOffListProps> = ({
  dropOffs,
  isLoading,
  error,
  onDropOffClick,
  onSelectDropOff,
}): JSX.Element => {
  const { isMobile } = useDetectDevice();
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number>(
    DEFAULT_EXPANDED_INDEX,
  );

  if (error) {
    return <PostalCodeInstruction text={t(error)} />;
  }

  if (isLoading) {
    return <PostalCodeInstruction text="Loading..." />;
  }

  if (dropOffs.length === 0) {
    return <PostalCodeInstruction text={t('RETURN_KEY_IN_YOUR_POSTAL_CODE')} />;
  }

  const handleAccordionChange = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(-1);
    } else {
      setExpandedIndex(index);
    }
    const dropOff = dropOffs[index];
    onDropOffClick(dropOff.dropOff.pudoLocationId);
  };

  return (
    <Accordion
      onChange={handleAccordionChange}
      collapsible
      defaultIndex={DEFAULT_EXPANDED_INDEX}
    >
      {dropOffs.map(({ dropOff }, index) => (
        <AccordionItem key={dropOff.pudoLocationId}>
          <AccordionButton isMobile={isMobile}>
            <DropOffCardButton
              dropOff={dropOff}
              isViewOnly={false}
              isShowDropoffMap={false}
              expanded={expandedIndex === index}
            />
          </AccordionButton>
          <AccordionPanel isMobile={isMobile}>
            <DropOffCardPanel
              dropOff={dropOff}
              onSelectDropOff={onSelectDropOff}
              isShowDropoffMap={false}
              isViewOnly={false}
            />
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export { DropOffList };
